/** Hours per case per day for the week, including time not released yet. */
import type { FC } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { FlatFeeTag } from "../board/BoardCard";
import { CaseDot } from "../components/CasePicker";
import { weekGrid } from "../model";
import type { TimeEntry } from "../types";
import { formatHours } from "../utils";

type Props = { entries: TimeEntry[]; dates: string[]; today: string };

const head = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return `${new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short"
  })} ${d}`;
};

const cell = (h: number) => (h ? String(Math.round(h * 100) / 100) : "–");

export const WeekTable: FC<Props> = ({ entries, dates, today }) => {
  const weekend = dates
    .slice(5)
    .some((d) =>
      entries.some((e) => e.work_date === d && e.status !== "discarded")
    );
  const shown = weekend ? dates : dates.slice(0, 5);
  const grid = weekGrid(entries, shown);
  const [y, m, d] = dates[0].split("-").map(Number);
  const weekOf = new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
  return (
    <div className="p-4" data-testid="week-table">
      <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
        <Table>
          <TableHeader className="bg-fill-1">
            <TableRow>
              <TableHead>Case</TableHead>
              {shown.map((iso) => (
                <TableHead
                  key={iso}
                  className={cn(
                    "w-19 text-right",
                    iso === today && "text-primary"
                  )}
                >
                  {head(iso)}
                </TableHead>
              ))}
              <TableHead className="w-21 text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.rows.map((r) => (
              <TableRow key={r.individual_id}>
                <TableCell>
                  <span className="flex min-w-0 items-center gap-2">
                    <CaseDot id={r.individual_id} />
                    <span className="truncate">{r.label}</span>
                    {r.flatFee ? (
                      <FlatFeeTag entry={{ fee_arrangement: "flat_fee" }} />
                    ) : null}
                  </span>
                </TableCell>
                {r.hours.map((h, i) => (
                  <TableCell
                    key={shown[i]}
                    className={cn("text-right tabular-nums", !h && "text-fg-4")}
                  >
                    {cell(h)}
                  </TableCell>
                ))}
                <TableCell className="text-right font-medium tabular-nums">
                  {cell(r.total)}
                </TableCell>
              </TableRow>
            ))}
            {grid.rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={shown.length + 2}
                  className="py-8 text-center text-fg-3"
                >
                  No time this week yet.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-medium">Total</TableCell>
              {grid.columns.map((t, i) => (
                <TableCell
                  key={shown[i]}
                  className="text-right font-medium tabular-nums"
                >
                  {cell(t)}
                </TableCell>
              ))}
              <TableCell className="text-right font-medium tabular-nums">
                {cell(grid.total)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <span className="mt-2 block text-sm text-fg-3">
        Hours by case, week of {weekOf}. Includes entries you haven't released
        yet.
        {grid.unassigned
          ? ` ${formatHours(grid.unassigned)} still needs a case.`
          : ""}
      </span>
    </div>
  );
};
